/**
 * Adjust cross–weight-class / cross–division wrestler comparisons.
 * Weight classes use the first numeric token as the class ceiling (lbs), matching
 * league weight setup and ikwfHistoryCsvImport.pickRankWeightClassForPounds.
 *
 * Division age order is best-effort; leagues should use labels from DIVISION_AGE_ORDER
 * or we skip adjustment when unknown.
 */

/** Younger / lower → older / higher within the same org league (League field). */
export const DIVISION_AGE_ORDER = [
  "tiny tot",
  "tiny",
  "pee wee",
  "bantam",
  "novice",
  "intermediate",
  "senior",
  "cadet",
  "schoolboy",
  "schoolgirl",
  "juvenile",
  "middle school",
  "junior high",
  "freshman",
  "sophomore",
  "junior",
  "varsity",
  "high school",
  /* IESA-style (larger school class ≈ tougher field; rough stand-in for age/size) */
  "a",
  "aa",
  "aaa",
];

const ELO_PER_WEIGHT_LB = 2.75;
const MAX_WEIGHT_ELO = 95;

const ELO_PER_DIVISION_STEP = 32;
const MAX_DIVISION_ELO = 110;

/**
 * @param {unknown} label
 * @returns {number|null}
 */
export function parseWeightClassLimit(label) {
  if (label == null || label === "") return null;
  const m = String(label).match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeDivisionKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * @param {string} divisionLabel from league Division field
 * @returns {number} index in DIVISION_AGE_ORDER, or -1 if unknown
 */
export function divisionAgeIndex(divisionLabel) {
  const key = normalizeDivisionKey(divisionLabel);
  if (!key) return -1;
  let idx = DIVISION_AGE_ORDER.indexOf(key);
  if (idx >= 0) return idx;
  /* Single-letter IESA classes often stored as "A " etc. */
  if (/^[a]{1,3}$/i.test(key)) {
    const compact = key.toLowerCase();
    if (compact === "a") return DIVISION_AGE_ORDER.indexOf("a");
    if (compact === "aa") return DIVISION_AGE_ORDER.indexOf("aa");
    if (compact === "aaa") return DIVISION_AGE_ORDER.indexOf("aaa");
  }
  return -1;
}

/**
 * Positive return value favors the opponent (raises effective skill gap vs "me").
 *
 * @param {{
 *   myWeightClass: unknown,
 *   theirWeightClass: unknown,
 *   myLeagueMeta: { league?: string, division?: string },
 *   theirLeagueMeta: { league?: string, division?: string },
 * }} params
 * @returns {{ eloShiftVsMe: number, weightDetail: string|null, divisionDetail: string|null }}
 */
export function computeMatchupContextAdjustment({
  myWeightClass,
  theirWeightClass,
  myLeagueMeta,
  theirLeagueMeta,
}) {
  let eloShiftVsMe = 0;
  let weightDetail = null;
  let divisionDetail = null;

  const wMe = parseWeightClassLimit(myWeightClass);
  const wThem = parseWeightClassLimit(theirWeightClass);
  if (wMe != null && wThem != null && wMe !== wThem) {
    const lbDiff = wThem - wMe;
    const raw = lbDiff * ELO_PER_WEIGHT_LB;
    const capped =
      raw > 0 ? Math.min(MAX_WEIGHT_ELO, raw) : Math.max(-MAX_WEIGHT_ELO, raw);
    eloShiftVsMe += capped;
    if (Math.abs(capped) >= 1) {
      if (lbDiff > 0) {
        weightDetail = `Their weight class is heavier (${wThem} vs ${wMe} lb cap), which favors them.`;
      } else {
        weightDetail = `Your weight class is heavier (${wMe} vs ${wThem} lb cap), which favors you.`;
      }
    }
  }

  const lm = myLeagueMeta || {};
  const tm = theirLeagueMeta || {};
  const parentMine = (lm.league && String(lm.league).trim()) || "";
  const parentTheirs = (tm.league && String(tm.league).trim()) || "";
  const divMine = (lm.division && String(lm.division).trim()) || "";
  const divTheirs = (tm.division && String(tm.division).trim()) || "";

  if (
    parentMine &&
    parentTheirs &&
    parentMine === parentTheirs &&
    divMine &&
    divTheirs &&
    divMine !== divTheirs
  ) {
    const iMe = divisionAgeIndex(divMine);
    const iThem = divisionAgeIndex(divTheirs);
    if (iMe >= 0 && iThem >= 0) {
      const steps = iThem - iMe;
      const raw = steps * ELO_PER_DIVISION_STEP;
      const capped =
        raw > 0
          ? Math.min(MAX_DIVISION_ELO, raw)
          : Math.max(-MAX_DIVISION_ELO, raw);
      eloShiftVsMe += capped;
      if (Math.abs(capped) >= 1) {
        if (steps > 0) {
          divisionDetail = `Their division (${divTheirs}) is older than yours (${divMine}) within ${parentMine}, which favors them.`;
        } else {
          divisionDetail = `Your division (${divMine}) is older than theirs (${divTheirs}) within ${parentMine}, which favors you.`;
        }
      }
    }
  }

  return { eloShiftVsMe, weightDetail, divisionDetail };
}
